import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import React, { createContext, ReactNode, useContext, useState } from 'react';

type IResponse = 'aborted' | 'confirmed' | 'rejected';

type Params = {
  title: string;
  message: string | JSX.Element;
  confirmButtonText: string;
  rejectButtonText?: string;
};

interface ConfirmationContextType {
  showConfirmation: (props: Params) => Promise<IResponse>;
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(
  undefined
);

interface ConfirmationProviderProps {
  children: ReactNode;
}

export const ConfirmationProvider: React.FC<ConfirmationProviderProps> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState<
    | (Params & {
        resolve: (_: IResponse) => void;
      })
    | null
  >(null);

  const showConfirmation = (props: Params): Promise<IResponse> => {
    return new Promise<IResponse>(resolve => {
      setDialogProps({ ...props, resolve });
      setOpen(true);
    });
  };

  const handleClose = (response: IResponse) => {
    setOpen(false);
    if (dialogProps?.resolve) {
      dialogProps.resolve(response);
    }
    setDialogProps(null);
  };

  return (
    <ConfirmationContext.Provider value={{ showConfirmation }}>
      {children}
      {dialogProps && (
        <Dialog open={open} onClose={() => handleClose('aborted')}>
          <DialogTitle>{dialogProps.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>{dialogProps.message}</DialogContentText>
          </DialogContent>
          <DialogActions>
            {dialogProps.rejectButtonText && (
              <Button
                variant='outlined'
                onClick={() => handleClose('rejected')}
              >
                {dialogProps.rejectButtonText}
              </Button>
            )}
            <Button
              variant='contained'
              onClick={() => handleClose('confirmed')}
              autoFocus
            >
              {dialogProps.confirmButtonText}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </ConfirmationContext.Provider>
  );
};

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error(
      'useConfirmation must be used within a ConfirmationProvider'
    );
  }
  return context;
};
